/**
 * Services API pour l'application appointments.
 * Toutes les requêtes passent par l'instance axios configurée dans api.ts.
 */

import api from './api';
import type {
  DisponibiliteMedecin,
  JourFerie,
  SessionWhatsApp,
  ConfigWhatsApp,
  MessageWhatsAppLog,
  FaqEntry,
  GrilleMedecin,
  PreviewCreneaux,
  StatistiquesWhatsApp,
} from '../types/appointments';

const BASE = '/appointments';

// ---- Disponibilités médecins ----

export const appointmentsApi = {
  // Grille complète pour l'interface secrétaire
  getGrille: () =>
    api.get<GrilleMedecin[]>(`${BASE}/disponibilites/grille/`).then(r => r.data),

  // Liste des disponibilités (filtrables par médecin)
  getDisponibilites: (doctorId?: number) =>
    api.get<DisponibiliteMedecin[]>(`${BASE}/disponibilites/`, {
      params: doctorId ? { doctor: doctorId } : {},
    }).then(r => r.data),

  // Créer une disponibilité
  createDisponibilite: (data: Partial<DisponibiliteMedecin>) =>
    api.post<DisponibiliteMedecin>(`${BASE}/disponibilites/`, data).then(r => r.data),

  // Mettre à jour une disponibilité
  updateDisponibilite: (id: number, data: Partial<DisponibiliteMedecin>) =>
    api.patch<DisponibiliteMedecin>(`${BASE}/disponibilites/${id}/`, data).then(r => r.data),

  // Supprimer une disponibilité
  deleteDisponibilite: (id: number) =>
    api.delete(`${BASE}/disponibilites/${id}/`),

  // Prévisualiser les créneaux pour un médecin/date
  previewCreneaux: (doctorId: number, date: string) =>
    api.get<PreviewCreneaux>(`${BASE}/disponibilites/creneaux-preview/`, {
      params: { doctor_id: doctorId, date },
    }).then(r => r.data),

  // ---- Jours fériés ----

  getJoursFeries: (aVenir = true) =>
    api.get<JourFerie[]>(`${BASE}/jours-feries/`, {
      params: { a_venir: aVenir ? 'true' : 'false' },
    }).then(r => r.data),

  createJourFerie: (data: Partial<JourFerie>) =>
    api.post<JourFerie>(`${BASE}/jours-feries/`, data).then(r => r.data),

  deleteJourFerie: (id: number) =>
    api.delete(`${BASE}/jours-feries/${id}/`),

  // ---- Questions fréquentes du bot ----

  getFaq: () =>
    api.get<FaqEntry[]>(`${BASE}/faq/`).then(r => r.data),

  saveFaq: (data: Partial<FaqEntry>) =>
    data.id
      ? api.patch<FaqEntry>(`${BASE}/faq/${data.id}/`, data).then(r => r.data)
      : api.post<FaqEntry>(`${BASE}/faq/`, data).then(r => r.data),

  deleteFaq: (id: number) =>
    api.delete(`${BASE}/faq/${id}/`),

  // ---- Configuration WhatsApp ----

  getConfig: () =>
    api.get<ConfigWhatsApp>(`${BASE}/config-whatsapp/`).then(r => r.data),

  saveConfig: (data: Partial<ConfigWhatsApp>) => {
    // Upsert via POST (singleton pk=1)
    return api.post<ConfigWhatsApp>(`${BASE}/config-whatsapp/`, data).then(r => r.data);
  },

  updateConfig: (id: number, data: Partial<ConfigWhatsApp>) =>
    api.patch<ConfigWhatsApp>(`${BASE}/config-whatsapp/${id}/`, data).then(r => r.data),

  getStatutConnexion: () =>
    api.get<{ connecte: boolean; statut: string; erreur: string | null }>(
      `${BASE}/config-whatsapp/statut-connexion/`
    ).then(r => r.data),

  // ---- Logs messages ----

  getLogs: (telephone?: string) =>
    api.get<MessageWhatsAppLog[]>(`${BASE}/messages-log/`, {
      params: telephone ? { telephone } : {},
    }).then(r => r.data),

  // ---- Sessions WhatsApp actives ----

  getSessions: () =>
    api.get<SessionWhatsApp[]>(`${BASE}/sessions-whatsapp/`).then(r => r.data),

  // L'accueil a fini : le bot reprend au prochain message du patient
  rendreAuBot: (id: number) =>
    api.post<SessionWhatsApp>(`${BASE}/sessions-whatsapp/${id}/rendre-au-bot/`).then(r => r.data),

  // ---- Activité du jour ----

  getStatistiques: () =>
    api.get<StatistiquesWhatsApp>(`${BASE}/config-whatsapp/statistiques/`).then(r => r.data),
};
